/**
 * # MenuListItem
 *
 * A component that represents a list item with hover and click states. It can display text, icons, and handle click events.
 *
 * ## Props
 *
 * - `as` (optional): The HTML element or React component to be rendered as the root element of the component. Default is `div`.
 * - `children` (optional): The content to be rendered inside the MenuListItem.
 * - `className` (optional): Additional CSS class names to be applied to the MenuListItem.
 * - `clickable` (optional): Determines if the MenuListItem is clickable. Default is `true`.
 * - `hoverable` (optional): Determines if the MenuListItem is hoverable. Default is `true`.
 * - `isCategoryItem` (optional): Determines if the MenuListItem is a category item. Default is `false`.
 * - `iconButtonProps` (optional): Additional props to be passed to the IconButton component used in the MenuListItem.
 * - `text` (optional): The text to be displayed in the MenuListItem.
 * - `icon` (optional): The icon to be displayed in the MenuListItem.
 * - `onMenuItemClick` (optional): The click event handler for the MenuListItem.
 * - `onIconButtonClick` (optional): The click event handler for the IconButton component used in the MenuListItem.
 * - `selected` (optional): Determines if the MenuListItem is selected. Default is `false`.
 * - `disableWhenSelected` (optional): Determines if the MenuListItem should be disabled when selected. Default is `true`.
 * - `indentLevel` (optional): The indent level of the MenuListItem. Can be '0', '1', or '2'.
 *
 * ## Usage
 *
 * ```tsx
 * import { MenuListItem } from './MenuListItem';
 *
 * const MyComponent = () => {
 *   return (
 *     <MenuListItem
 *       text="Menu Item"
 *       icon="icon-name"
 *       onMenuItemClick={() => {
 *         console.log('Menu item clicked');
 *       }}
 *     />
 *   );
 * };
 * ```
 */

import classnames from 'classnames';

import React, {forwardRef} from 'react';

import IconButton, {IconButtonProps} from '@albert-io/atomic/molecules/IconButton/IconButton.react';

import Icon, {IconProp} from '../Icon/Icon.react';
import Text, {Props as TextProps} from '../Text/Text.react';

import './menu-list-item.scss';

type InnerIndentLevel = 0 | 1 | 2;
interface TextPrimitiveProps {
  className?: string;
  children?: React.ReactNode;
  indentLevel?: InnerIndentLevel;
  selected?: boolean;
  size?: TextProps['size'];
}

const RegularText = ({
  className,
  indentLevel = 0,
  selected = false,
  children,
  ...rest
}: TextPrimitiveProps) => (
  <Text
    bold
    className={classnames(className, `u-mar-l_${indentLevel * 2}`)}
    emphasis='high'
    size='m'
    color={selected ? 'blue' : 'black'}
    {...rest}
  >
    {children}
  </Text>
);

const CategoryItemText = ({className, indentLevel = 0, children, ...rest}: TextPrimitiveProps) => (
  <Text
    className={classnames(className, `u-mar-l_${indentLevel * 2}`)}
    bold
    size='m'
    emphasis='lowest'
    {...rest}
  >
    {children}
  </Text>
);

type HandleButtonClickType = () => void;

// requires button label along with icon button click handler for aria support
type ButtonClickProps = {
  onIconButtonClick?: HandleButtonClickType;
  iconButtonLabel: string;
};

type ConditionalProps =
  | ButtonClickProps
  | {
      onIconButtonClick?: never;
      iconButtonLabel?: never;
    };

const isOnButtonClick = (onClick: any): onClick is HandleButtonClickType => {
  if (!onClick && typeof onClick !== 'function') {
    return false;
  }
  return true;
};

type CommonProps<C extends React.ElementType = 'div'> = {
  as?: C;
  children?: React.ReactNode;
  className?: string;
  clickable?: boolean;
  hoverable?: boolean;
  isCategoryItem?: boolean;
  /* Is needed to pass Ids to iconButton used in 'Expandable' for 'expandableMenuListItem' */
  iconButtonProps?: Partial<IconButtonProps<'button'>>;
  text?: string;
  icon?: IconProp;
  // onClick applied to entire item
  onMenuItemClick?: () => void; // TODO: determine if this needs to pass an event
  // onclick applied to the button
  onIconButtonClick?: () => void;
  selected?: boolean;
  disableWhenSelected?: boolean;
  indentLevel?: '0' | '1' | '2';
};

type Props<C extends React.ElementType> = CommonProps<C> & ConditionalProps;

export const MenuListItem: React.ForwardRefExoticComponent<
  React.PropsWithChildren<Props<React.ElementType<any>>> & React.RefAttributes<unknown>
> & {Text: typeof RegularText; CategoryItemText: typeof CategoryItemText} = Object.assign(
  forwardRef(
    <C extends React.ElementType = 'div'>(
      {
        as,
        clickable = true,
        hoverable = true,
        text = '',
        onIconButtonClick,
        onMenuItemClick,
        selected = false,
        disableWhenSelected = true,
        className,
        icon,
        indentLevel,
        isCategoryItem,
        iconButtonLabel,
        iconButtonProps,
        children,
        ...rest
      }: Props<C>,
      ref
    ) => {
      function handleIconButtonClick(e) {
        if (isOnButtonClick(onIconButtonClick)) {
          onIconButtonClick();
        }
        e.stopPropagation();
      }

      const WrapperElement = as || 'div';
      const menuItemRef = ref ?? React.createRef();

      function getChildren() {
        /**
         * Component primitives
         * ===
         * If the developer is opting to pass custom content between the <MenuListItem></MenuListItem> tags,
         * then we will skip the auto-configured set-up and defer and simply return the children (while also
         * cloning the relevant style props down to them, such as those required by <MenuListItem.Text />
         */
        if (!children) {
          return (
            <>
              {text && !isCategoryItem && (
                <MenuListItem.Text
                  indentLevel={indentLevel ? (+indentLevel as InnerIndentLevel) : undefined}
                  selected={selected}
                >
                  {text}
                </MenuListItem.Text>
              )}

              {text && isCategoryItem && (
                <MenuListItem.CategoryItemText
                  indentLevel={indentLevel ? (+indentLevel as InnerIndentLevel) : undefined}
                  selected={selected}
                >
                  {text}
                </MenuListItem.CategoryItemText>
              )}

              {icon && isOnButtonClick(onIconButtonClick) && iconButtonLabel && (
                <IconButton
                  className={classnames('a-menu-list-item--icon', {
                    'u-mar-l_2': text
                  })}
                  label={iconButtonLabel}
                  onClick={handleIconButtonClick}
                  icon={icon}
                  {...iconButtonProps}
                />
              )}

              {icon && !onIconButtonClick && (
                <div
                  className={classnames('a-menu-list-item--icon-wrapper', {
                    'u-mar-l_2': text
                  })}
                >
                  <Icon icon={icon} />
                </div>
              )}
            </>
          );
        }

        return React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              isCategoryItem,
              indentLevel,
              selected
            } as CommonProps);
          }
          return null;
        });
      }

      // Since we do have support for an iconButton to be a child we need this to make sure that keypresses on the iconButtons don't activate the parent
      function handleKeyPress(e) {
        const keys = [' ', 'Enter'];
        if (global.document.activeElement === menuItemRef?.current && keys.includes(e.key)) {
          e.preventDefault();
          onMenuItemClick?.();
        }
      }

      return (
        <WrapperElement
          className={classnames(className, 'a-menu-list-item', 'u-pad-l_2', {
            'a-menu-list-item--hoverable': hoverable,
            'a-menu-list-item--clickable': clickable,
            'a-menu-list-item--selected': selected,
            'a-menu-list-item--disabled': disableWhenSelected && selected,
            'u-pad-tb_1': icon,
            'u-pad-r_1': icon && text,
            'u-pad-r_2': !icon || (icon && !text),
            'u-justify-content_center': !text,
            'u-justify-content_space-between': text
          })}
          ref={menuItemRef}
          data-name='menu-list-item'
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          onClick={onMenuItemClick}
          onKeyPress={handleKeyPress}
          {...(WrapperElement === 'div'
            ? {}
            : {
                disabled: disableWhenSelected && selected
              })}
          {...rest}
        >
          {getChildren()}
        </WrapperElement>
      );
    }
  ),
  {
    Text: RegularText,
    CategoryItemText
  }
);

export default MenuListItem;
